import { useEffect, useState } from "react";
import { Building2, Crown, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";

export function CompanyBoard() {
  const [d, setD] = useState(null);
  const [name, setName] = useState("");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/companies/me").then((r) => setD(r.data)).catch(() => {}); }, []);

  const run = async (req) => {
    setBusy(true);
    try {
      setD((await req()).data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };
  if (!d) return null;

  if (!d.company)
    return d.can_manage ? (
      <section className="bg-white rounded-2xl border-2 border-slate-200 p-4 space-y-3 rise" data-testid="company-setup">
        <p className="flex items-center gap-2 text-xs font-bold uppercase text-slate-500"><Building2 className="w-4 h-4" /> Classement entre chantiers</p>
        <div className="flex gap-2">
          <Input value={code} onChange={(e) => setCode(e.target.value.toUpperCase().slice(0, 6))} placeholder="CODE ENTREPRISE" className="h-11 font-bold tracking-widest" data-testid="company-join-input" />
          <button disabled={busy || code.length !== 6} onClick={() => run(() => api.post("/companies/join", { code }))} data-testid="company-join-btn" className="h-11 px-4 rounded-xl bg-orange-500 text-white font-bold disabled:opacity-40">Rejoindre</button>
        </div>
        <div className="flex gap-2">
          <Input value={name} onChange={(e) => setName(e.target.value)} maxLength={60} placeholder="Nom de l'entreprise" className="h-11" data-testid="company-create-input" />
          <button disabled={busy || !name.trim()} onClick={() => run(() => api.post("/companies", { name }))} data-testid="company-create-btn" className="h-11 px-4 rounded-xl bg-slate-900 text-white font-bold disabled:opacity-40">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Créer"}
          </button>
        </div>
      </section>
    ) : null;

  return (
    <section className="bg-white rounded-2xl border-2 border-slate-200 p-4 space-y-3 rise" data-testid="company-board">
      <div className="flex items-center justify-between">
        <p className="flex items-center gap-2 text-xs font-bold uppercase text-slate-500"><Building2 className="w-4 h-4" /> {d.company.name}</p>
        <span className="text-xs font-bold text-slate-500" data-testid="company-code">Code : {d.company.code}</span>
      </div>
      <p className="font-display text-xl font-bold uppercase">Classement des chantiers · semaine</p>
      {d.teams.map((t, i) => (
        <div key={`${t.name}-${i}`} data-testid={`company-team-${i}`} className={`flex items-center gap-3 rounded-xl border-2 p-3 ${t.is_mine ? "bg-orange-50 border-orange-400" : "border-slate-200"}`}>
          <span className="w-9 h-9 rounded-full bg-slate-900 text-amber-400 grid place-items-center font-display text-lg font-bold">{i === 0 ? <Crown className="w-5 h-5" /> : i + 1}</span>
          <div className="flex-1 min-w-0">
            <div className="font-bold truncate">{t.name}{t.is_mine && " (nous)"}</div>
            <div className="text-xs text-slate-500">{t.members} pers. · {t.week_lessons} leçons · {t.trophies} trophée(s)</div>
          </div>
          <div className="font-display text-2xl font-bold text-orange-600" data-testid={`company-team-score-${i}`}>{t.per_member}</div>
        </div>
      ))}
      <p className="text-[11px] text-slate-500">Score = leçons de la semaine par personne. Partagez le code avec les autres chefs.</p>
      {d.can_manage && (
        <button onClick={() => run(() => api.post("/companies/leave"))} data-testid="company-leave-btn" className="text-xs font-semibold text-slate-500 hover:text-red-600">Quitter l'entreprise</button>
      )}
    </section>
  );
}
